<template>
  <section class="grid grid-rows-[auto_1fr] h-full bg-white">
    <BaseDetailsPreviewEditHeader
      :is-horizontal-layout="false"
      hideLayoutOptions
      :mode="mode"
      :is-edit-active="!!selectedGroup?.name"
      @edit-mode="mode = 'edit'"
      @save="onGroupsFormSave"
      @cancel="onGroupsFormCancel" />

    <section class="h-full overflow-hidden">
      <div class="grid w-full h-full grid-cols-[1fr_1fr_2fr] divide-x" id="grouping-main">
        <div class="h-full grid grid-rows-[auto_1fr] bg-gray-100" id="groups">
          <div class="px-4 py-2 bg-white border-b-4 border-gray-200" id="groups-heading">
            <p class="text-base text-gray-600">{{ $t('groups.groups') }}</p>
          </div>
          <GroupsList />
        </div>

        <div class="h-full overflow-y-auto">
          <GroupItems />
        </div>

        <div class="flex flex-col overflow-hidden" id="details">
          <div class="px-4 py-2 bg-white border-b-4 border-gray-200" id="groups-heading">
            <p class="text-base text-gray-600">Details {{ selectedGroupItem?.name || '' }}</p>
          </div>
          <div class="h-full px-1 overflow-x-hidden overflow-y-auto">
            <GroupItemDetails />
          </div>
        </div>
      </div>
    </section>

    <BaseDrawerConfirm
      :is-active="modalCloseWarningActive"
      @confirm="onDismissChanges"
      @cancel="modalCloseWarningActive = false" />
  </section>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { storeToRefs } from 'pinia';
import { useGroupingStore } from '@/stores/grouping';
import { useLookupsStore } from '@/stores/lookups';
import {
  addOrUpdateGroupCollection,
  getGroupItemMaterialForGroupItemId,
  getGroupItemsAlterCodeForGroupItemId,
  getGroupItemsByGroup,
  getGroupItemsCustomerForGroupItemId,
  getGroupItemsTraderSiteForGroupItemId,
  getGroupsByCategory,
} from '@/services/api/groups';

import BaseDetailsPreviewEditHeader from '@/components/common/BaseDetailsPreviewEditHeader.vue';
import GroupsList from '@/components/grouping/GroupsList.vue';
import GroupItems from '@/components/grouping/GroupItems.vue';
import GroupItemDetails from '@/components/grouping/GroupItemDetails.vue';
import BaseDrawerConfirm from '@/components/common/BaseDrawerConfirm.vue';
import { type Group } from '@/types/Groups';
import { type GroupCategory } from '@/types/Base';
import { useToastStore } from '@/stores/toast';
import { deepCopy } from '@/utils/data';

const route = useRoute();

const {
  groups,
  mode,
  category,
  modifiedGroups,
  selectedGroup,
  groupItems,
  groupItemNodes,
  selectedGroupItem,
  groupItemsMaterial,
  groupItemsMaterialTotalCount,
  groupItemsAlterCode,
  groupItemsAlterCodeTotalCount,
  groupItemsCustomer,
  groupItemsCustomerTotalCount,
  groupItemsTraderSite,
  groupItemsTraderSiteTotalCount,
} = storeToRefs(useGroupingStore());

const { resetGroupModesToView, initializeGroup } = useGroupingStore();

const stateGroups = ref<Group[]>([]);

const { getMwAccountGroupsLookup } = useLookupsStore();

async function loadData() {
  await getMwAccountGroupsLookup();
  await getGroups();
}

onMounted(async () => {
  await loadData();
});

async function getGroups() {
  modifiedGroups.value = [];
  category.value = route.params.category as GroupCategory;
  const res = await getGroupsByCategory(route.params.category as string);
  if (res) {
    groups.value = res.data;
    stateGroups.value = deepCopy(groups.value);
  }
}

onUnmounted(() => {
  groups.value = [];
  groupItemNodes.value = [];
  selectedGroupItem.value = undefined;
  category.value = '';
});

watch(
  () => route.params.category,
  async (newData) => {
    onDismissChanges();
    groups.value = [];
    category.value = newData as GroupCategory;
    groupItemNodes.value = [];
    const res = await getGroupsByCategory(newData as string);
    if (res) {
      groups.value = res.data;
      stateGroups.value = deepCopy(groups.value);
    }
  }
);

const isModified = ref(false);
const modalCloseWarningActive = ref(false);

function onDismissChanges() {
  groups.value = stateGroups.value;
  groupItemNodes.value = [];
  modifiedGroups.value = [];
  selectedGroup.value = initializeGroup();
  selectedGroupItem.value = undefined;
  modalCloseWarningActive.value = false;
  mode.value = 'view';
}

async function onGroupsFormCancel() {
  isModified.value = modifiedGroups.value.length > 0;
  if (isModified.value) modalCloseWarningActive.value = true;
  else {
    await getGroups();
    mode.value = 'view';
  }
}

const toastStore = useToastStore();

async function onGroupsFormSave() {
  const res = await addOrUpdateGroupCollection(modifiedGroups.value);

  if (res) {
    toastStore.success({ text: 'Save successful' });
    if (selectedGroupItem.value) {
      selectedGroupItem.value.groupItemMaterials = [];
      selectedGroupItem.value.groupItemAlterCodes = [];
      selectedGroupItem.value.groupItemCustomers = [];
      selectedGroupItem.value.groupItemTraderSites = [];
    }
    groupItemsTraderSite.value = [];
    groupItemsTraderSiteTotalCount.value = undefined;
    groupItemsMaterial.value = [];
    groupItemsMaterialTotalCount.value = undefined;
    groupItemsAlterCode.value = [];
    groupItemsAlterCodeTotalCount.value = undefined;
    groupItemsCustomer.value = [];
    groupItemsCustomerTotalCount.value = undefined;
    groupItemsTraderSite.value = [];
    groupItemsTraderSiteTotalCount.value = undefined;
    await getGroups();
    resetGroupModesToView();
    if (selectedGroup.value) {
      const res = await getGroupItemsByGroup(selectedGroup.value.id);
      groupItems.value = res.data || [];
    }
    if (selectedGroupItem.value) {
      await refreshGroupItemDetails();
    }

    if (selectedGroup.value) {
      selectedGroup.value.groupItems = [];
    }
    if (modifiedGroups.value && modifiedGroups.value.length > 0) {
      modifiedGroups.value.forEach((group) => {
        if (group) {
          group.groupItems = [];
        }
      });
    }
  } else {
    toastStore.error({ text: 'Something went wrong while saving' });
  }
  mode.value = 'view';
}

async function refreshGroupItemDetails() {
  if (!selectedGroupItem.value) return;

  const groupItemId = selectedGroupItem.value.id;

  switch (category.value) {
    case 'material':
      {
        const resMaterial = await getGroupItemMaterialForGroupItemId({
          itemsPerPage: 10,
          groupItemId: groupItemId,
        });
        if (resMaterial) {
          groupItemsMaterial.value = resMaterial.data;
          groupItemsMaterialTotalCount.value = resMaterial.count;
        }
      }
      break;

    case 'alterCode':
      {
        const resAlterCode = await getGroupItemsAlterCodeForGroupItemId({
          itemsPerPage: 10,
          groupItemId: groupItemId,
        });
        if (resAlterCode) {
          groupItemsAlterCode.value = resAlterCode.data;
          groupItemsAlterCodeTotalCount.value = resAlterCode.count;
        }
      }
      break;

    case 'customer':
      {
        const resCustomer = await getGroupItemsCustomerForGroupItemId({
          itemsPerPage: 10,
          groupItemId: groupItemId,
        });
        if (resCustomer) {
          groupItemsCustomer.value = resCustomer.data;
          groupItemsCustomerTotalCount.value = resCustomer.count;
        }
      }
      break;

    case 'traderSite':
      {
        const resTraderSite = await getGroupItemsTraderSiteForGroupItemId({
          itemsPerPage: 10,
          groupItemId: groupItemId,
        });
        if (resTraderSite) {
          groupItemsTraderSite.value = resTraderSite.data;
          groupItemsTraderSiteTotalCount.value = resTraderSite.count;
        }
      }
      break;

    default:
      break;
  }
}
</script>
